// One evaluation case in its suite's list (ADR 0035): its verdict, name and question, and what can be done to it —
// Disable (kept, not run) or Enable again, and Delete after a confirmation (one change; past runs keep
// their results), through the host's evals port.
import { Loader2, MoreHorizontal, Trash2 } from 'lucide-react';
import * as React from 'react';
import { StatusBadge, type EvalCase } from './Evaluations.tsx';
import { useAgentsHost } from './host.tsx';
import { btnGhost, iconBtn, messageOf } from './ui.tsx';

export function CaseRow({
  c,
  onChanged,
  children,
}: {
  c: EvalCase;
  onChanged: () => void;
  children?: React.ReactNode;
}) {
  const port = useAgentsHost().evals;
  const [menu, setMenu] = React.useState(false);
  const [confirm, setConfirm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const on = c.enabled !== false;
  const act = async (f: () => Promise<void>) => {
    setBusy(true);
    setErr('');
    setMenu(false);
    try {
      await f();
      setConfirm(false);
      onChanged();
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const toggle = () =>
    port?.setCaseEnabled ? act(() => port.setCaseEnabled!({ key: c.key, enabled: !on })) : Promise.resolve();
  const remove = () => (port?.removeCase ? act(() => port.removeCase!({ key: c.key })) : Promise.resolve());
  return (
    <li
      className="flex items-start gap-2"
      data-testid={`evals-suite-case-${c.key}`}
      data-enabled={String(on)}
    >
      <StatusBadge status={c.latest?.status ?? null} />
      <span className="min-w-0 flex-1">
        <span className="text-page-text">{c.name}</span>
        <span className="text-page-muted"> — “{c.prompt}”</span>
        {children}
        {confirm ? (
          <div
            className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-red-300 bg-red-50 px-2 py-1.5 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
            data-testid={`evals-case-confirm-${c.key}`}
          >
            <span className="flex-1 min-w-48">
              Delete “{c.name}”? It is removed from the app (one change). Past runs keep their results.
            </span>
            <button type="button" className={btnGhost} onClick={() => setConfirm(false)} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
              data-testid={`evals-case-delete-${c.key}`}
              disabled={busy}
              onClick={() => void remove()}
            >
              {busy ? <Loader2 size={12} className="inline animate-spin" /> : null} Delete
            </button>
          </div>
        ) : null}
        {err ? <div className="mt-1 text-xs text-red-600">{err}</div> : null}
      </span>
      {on ? (
        <span className="relative">
          <button
            type="button"
            className={iconBtn}
            aria-label={`Actions of ${c.name}`}
            data-testid={`evals-case-menu-${c.key}`}
            onClick={() => setMenu(!menu)}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <MoreHorizontal size={14} />}
          </button>
          {menu ? (
            <span className="absolute right-0 top-7 z-10 grid min-w-48 rounded-md border border-card-border bg-card-bg py-1 text-sm shadow-lg">
              {port?.setCaseEnabled ? (
                <button
                  type="button"
                  className="px-3 py-1.5 text-left bg-transparent border-0 cursor-pointer text-page-text hover:bg-card-border/30"
                  data-testid={`evals-case-disable-${c.key}`}
                  onClick={() => void toggle()}
                >
                  Disable — keep, don’t run
                </button>
              ) : null}
              {port?.removeCase ? (
                <button
                  type="button"
                  className="px-3 py-1.5 text-left bg-transparent border-0 cursor-pointer text-red-600 hover:bg-card-border/30"
                  data-testid={`evals-case-ask-delete-${c.key}`}
                  onClick={() => (setMenu(false), setConfirm(true))}
                >
                  Delete…
                </button>
              ) : null}
            </span>
          ) : null}
        </span>
      ) : (
        <span className="flex items-center gap-1">
          {port?.setCaseEnabled ? (
            <button
              type="button"
              className={btnGhost}
              data-testid={`evals-case-enable-${c.key}`}
              disabled={busy}
              onClick={() => void toggle()}
            >
              Enable
            </button>
          ) : null}
          {port?.removeCase ? (
            <button
              type="button"
              className={iconBtn}
              aria-label={`Delete ${c.name}`}
              data-testid={`evals-case-ask-delete-${c.key}`}
              onClick={() => setConfirm(true)}
            >
              <Trash2 size={14} />
            </button>
          ) : null}
        </span>
      )}
    </li>
  );
}
